// src/pages/UsersPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  IconButton,
  Tooltip,
  Stack,
  Skeleton,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from '@mui/icons-material/People';
import HandymanIcon from '@mui/icons-material/Handyman';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PushPinIcon from '@mui/icons-material/PushPin';
import CrmDialog from "../components/CrmDialog";
import { useTheme } from "@mui/material/styles";


import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import { getUserInitials } from "../utils/userInitials";

export default function UsersPage() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editGroups, setEditGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLandscape = useMediaQuery("(orientation: landscape)");
const navigate = useNavigate();

const mobileUserCardSx = {
  p: 2.25,
  borderRadius: 3,

  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(30,41,59,0.88)"   // slate-800 glass
      : "background.paper",

  border: "1px solid",
  borderColor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",

  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 8px 24px rgba(0,0,0,0.55)"
      : "0 4px 12px rgba(0,0,0,0.12)",

  transition: "box-shadow 0.2s ease, transform 0.2s ease",

  "&:active": {
    transform: "scale(0.995)",
  },
};



  // Search state
  const [searchFilters, setSearchFilters] = useState({
    name: "",
    role: "",
    status: "",
  });

  // ---------- helpers ----------
  const normalizeToIds = (valueList, groupsList) => {
    const arr = Array.isArray(valueList) ? valueList : valueList ? [valueList] : [];
    const ids = arr
      .map((v) => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string") {
          const n = Number(v);
          if (Number.isFinite(n)) return n;
          const byName = groupsList.find((g) => g.name === v);
          if (byName) return byName.id;
          const byIdString = groupsList.find((g) => String(g.id) === v);
          if (byIdString) return byIdString.id;
          return null;
        }
        if (v && typeof v === "object") {
          if (typeof v.id === "number") return v.id;
        }
        return null;
      })
      .filter((id) => typeof id === "number" && Number.isFinite(id));
    return Array.from(new Set(ids));
  };

  const idsToNames = (ids, groupsList) =>
    (ids || [])
      .map((id) => groupsList.find((g) => g.id === id)?.name || null)
      .filter(Boolean);

  // Build role options dynamically from /groups/ to avoid drift
  const roleOptions = useMemo(
    () => groups.map((g) => ({ id: g.id, name: g.name })),
    [groups]
  );

  // ---------- data fetch ----------
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await API.get("/users/");
      setUsers(
  res.data.map(u => ({
    ...u,
    mobile_status: u.is_active ? "Active" : "Inactive",  // ⭐ REQUIRED
  }))
);

      setFilteredUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await API.get("/groups/");
      setGroups(res.data || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
      toast.error("Failed to load groups.");
    }
  };

  const fetchTerritories = async () => {
    try {
      const res = await API.get("/territories/");
      setTerritories(res.data || []);
    } catch (error) {
      console.error("Error fetching territories:", error);
      toast.error("Failed to load territories.");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchTerritories();
  }, []);

  // ---------- search and filtering logic ----------
  const applyFilters = (userList) => {
    return userList.filter((user) => {
      const nameMatch =
        !searchFilters.name ||
        `${user.first_name || ""} ${user.last_name || ""}`
          .toLowerCase()
          .includes(searchFilters.name.toLowerCase());

      // Use robust normalization (handles ids/objects/strings)
      const normalizedUserGroupIds = normalizeToIds(user.groups || [], groups);
      const roleMatch =
        !searchFilters.role ||
        normalizedUserGroupIds.includes(Number(searchFilters.role));

      const statusMatch =
        !searchFilters.status ||
        (searchFilters.status === "active" ? user.is_active : !user.is_active);

      return nameMatch && roleMatch && statusMatch;
    });
  };

  const handleSearch = () => setFilteredUsers(applyFilters(users));

  const handleClearSearch = () => {
    setSearchFilters({ name: "", role: "", status: "" });
    setFilteredUsers(users);
  };

  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };
useEffect(() => {
  setFilteredUsers(applyFilters(users));
}, [users, searchFilters, groups]);


  // ---------- delete ----------
  const handleConfirmDelete = (user) => {
    setUserToDelete(user);
    setConfirmDelete(true);
  };

  const handleDeleteUser = async () => {
    try {
      await API.delete(`/users/${userToDelete.id}/`);
      setConfirmDelete(false);
      setOpenEdit(false);
      toast.success("User deleted.");
      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error("Failed to delete user.");
    }
  };

  // ---------- helper functions for table rendering ----------
  const renderUserCell = (user) => (
    <Box display="flex" alignItems="center" gap={2}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: "primary.main",
          color: "#fff",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        {getUserInitials(
          {
            first_name: user.first_name,
            last_name: user.last_name,
            full_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
            username: user.username,
          },
          "U"
        )}
      </Box>
      <Box>
        <Typography fontWeight={600}>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user.email}
        </Typography>
      </Box>
    </Box>
  );

  const renderRoleCell = (user) => {
    const raw = user.groups || [];
    const ids = normalizeToIds(raw, groups);
    const names = idsToNames(ids, groups);

    if (!names.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {names.map((name) => (
          <Chip
            key={name}
            label={name}
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: "rgba(33,150,243,0.12)",
              color: "primary.main",
              borderRadius: "6px",
            }}
          />
        ))}
      </Box>
    );
  };

  // Get all territory names for a user
  const getTerritoryNames = (user) => {
    const names = [];
    
    // ✅ NEW: Get from territories_names (from API)
    if (user.territories_names && Array.isArray(user.territories_names) && user.territories_names.length > 0) {
      return user.territories_names;
    }
    
    // ✅ NEW: Get from territories array (objects or IDs)
    if (user.territories && Array.isArray(user.territories) && user.territories.length > 0) {
      user.territories.forEach(t => {
        if (typeof t === 'object' && t.name) {
          names.push(t.name);
        } else if (typeof t === 'number') {
          const found = territories.find(ter => ter.id === t);
          if (found) names.push(found.name);
        }
      });
      if (names.length > 0) return names;
    }
    
    // ⚠️ LEGACY: Fallback to single territory
    if (user.territory?.name) return [user.territory.name];
    if (user.territory_name) return [user.territory_name];
    const found = territories.find((t) => t.id === user.territory);
    if (found) return [found.name];
    
    return [];
  };

  // Get primary/first territory name for display (backward compatibility)
  const getTerritoryName = (user) => {
    const names = getTerritoryNames(user);
    return names.length > 0 ? names[0] : "—";
  };

  const renderStatusCell = (user) => {
    const active = Boolean(user?.is_active);
    return (
      <Chip
        label={active ? "Active" : "Inactive"}
        size="small"
        sx={{
          fontWeight: 600,
          bgcolor: active
            ? "rgba(56,142,60,0.15)"
            : "rgba(180,0,0,0.15)",
          color: active ? "#388E3C" : "#B71C1C",
          borderRadius: "6px",
        }}
      />
    );
  };

  const renderTerritoryCell = (user) => {
    const territoryNames = getTerritoryNames(user);
    
    // No territories
    if (territoryNames.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }
    
    // Single territory - no tooltip needed
    if (territoryNames.length === 1) {
      return (
        <Typography variant="body2">
          {territoryNames[0]}
        </Typography>
      );
    }
    
    // Multiple territories - show first with hover tooltip
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
              All Territories ({territoryNames.length}):
            </Typography>
            {territoryNames.map((name, index) => (
              <Typography key={index} variant="body2" sx={{ display: "block", mb: 0.25 }}>
                • {name}
              </Typography>
            ))}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "help",
          }}
        >
          <Typography variant="body2">
            {territoryNames[0]}
          </Typography>
          <Chip
            label={`+${territoryNames.length - 1}`}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: "primary.main",
              color: "#fff",
              "& .MuiChip-label": {
                px: 0.75,
              },
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  // ---------- edit modal logic ----------
  const handleRowClick = (user) => {
    const territoryId = user.territory?.id ?? user.territory ?? "";
    const userWithTerritory = { ...user, territory: territoryId };
    setSelectedUser(userWithTerritory);
    const normalized = normalizeToIds(user.groups || [], groups);
    setEditGroups(normalized);
    setOpenEdit(true);
  };

  useEffect(() => {
    if (openEdit && selectedUser) {
      const normalized = normalizeToIds(selectedUser.groups || [], groups);
      setEditGroups(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const handleEditField = (field, value) => {
    setSelectedUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleTerritoryChange = (territoryId) => {
  setSelectedUser((prev) => ({
    ...prev,
    territory: Number.isFinite(territoryId) ? territoryId : null,
  }));
};


  const handleSaveUser = async () => {
    try {
      const numericGroupIds = normalizeToIds(editGroups, groups);
      const payload = {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        username: selectedUser.username,
        email: selectedUser.email,
        is_active: !!selectedUser.is_active,
        groups: numericGroupIds,
        territory_id: selectedUser.territory ? parseInt(selectedUser.territory) : null,
      };

      const response = await API.patch(`/users/${selectedUser.id}/`, payload);
      toast.success("User updated.");
      const updatedUser = response.data;
setUsers((prev) =>
  prev.map((u) =>
    u.id === selectedUser.id
      ? {
          ...u,
          territory: payload.territory,
          territory_name:
            territories.find((t) => t.id === payload.territory)?.name || "",
        }
      : u
  )
);
setFilteredUsers((prev) =>
  prev.map((u) =>
    u.id === selectedUser.id
      ? {
          ...u,
          territory: payload.territory,
          territory_name:
            territories.find((t) => t.id === payload.territory)?.name || "",
        }
      : u
  )
);

      setOpenEdit(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err?.response?.data || err);
      toast.error("Failed to update user.");
    }
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {/* HEADER CARD */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(25,118,210,0.08)",
              }}
            >
              <PeopleIcon color="primary" />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Users
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage users, roles, and access ({filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"})
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            onClick={() => navigate("/users/new")}
            sx={{
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.875rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.4,
              padding: "8px 16px",
              whiteSpace: "nowrap",
              width: { xs: "100%", md: "auto" },
              mt: { xs: 2, md: 0 },
              transition: "all 0.18s ease",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "#fff",
              boxShadow: (theme) => `0 10px 22px ${theme.palette.primary.main}47`,
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: (theme) => `0 14px 28px ${theme.palette.primary.main}61`,
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: (theme) => `0 6px 14px ${theme.palette.primary.main}40`,
              },
            }}
          >
            + Add User
          </Button>
        </Stack>
      </Card>

      {/* --------------------------------------------------------------FILTERS CARD - Streamlined tabs + search */}
      <Card
        data-tour="users-toolbar"
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Row 1: Search */}
          <TextField
            label="Search Name / Username"
            placeholder="Search users..."
            value={searchFilters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "28px",
                backgroundColor: "background.paper",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.08)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.15)",
                },
                "&.Mui-focused fieldset": {
                  borderWidth: "1px",
                },
              },
            }}
          />

          {/* Row 2: Status + Role pills (one row on desktop; Status only on mobile) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "flex-start" },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                Status
              </Typography>
              <ToggleButtonGroup
                value={searchFilters.status}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue != null) handleFilterChange("status", newValue);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButtonGroup-grouped": {
                    borderRadius: "20px !important",
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1.5,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    bgcolor: "action.selected",
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  },
                }}
              >
                <ToggleButton value="">All</ToggleButton>
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="inactive">Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Role pills: desktop only, in line with Status */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                Role
              </Typography>
              <ToggleButtonGroup
                value={searchFilters.role === "" || searchFilters.role == null ? "" : String(searchFilters.role)}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue != null) handleFilterChange("role", newValue);
                }}
                size="small"
                sx={{
                  flexWrap: "wrap",
                  gap: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    borderRadius: "20px !important",
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1.5,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    bgcolor: "action.selected",
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  },
                }}
              >
                <ToggleButton value="">All</ToggleButton>
                {roleOptions.map((role) => (
                  <ToggleButton key={role.id} value={String(role.id)}>
                    {role.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* --------------------------------------------------------TABLE CARD - Material Dashboard Style */}
      <Card
        data-tour="users-table"
        sx={{
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          overflow: "visible",
          "& .MuiCard-root": {
            overflow: "visible",
          },
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" width="30%" sx={{ mb: 2 }} />
            {isMobile ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            ) : (
              <Box sx={{ px: 2 }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            )}
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" gutterBottom>No users yet.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add your first user to get started.</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate("/users/new")}>
              Add your first user
            </Button>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                mb: 2,
                px: 2,
                pt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {filteredUsers.length} of {users.length} users
                {filteredUsers.length !== users.length && " (filtered)"}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", overflow: "auto" }}>
              {isMobile ? (
                // ------------------------------------------------------------------------------------Mobile Card View
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1, sm: 2 },
                  }}
                >
                  {filteredUsers.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary" gutterBottom>
                        {users.length === 0 ? "No users yet." : "No users found."}
                      </Typography>
                      {users.length === 0 && (
                        <Button variant="contained" color="primary" onClick={() => navigate("/users/new")} sx={{ mt: 1 }}>
                          Add your first user
                        </Button>
                      )}
                    </Box>
                  ) : (
                    filteredUsers.map((user) => (
                      <Card
  key={user.id}
  sx={mobileUserCardSx}
>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                          {/* Identity block */}
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                color: "#fff",
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                flexShrink: 0,
                              }}
                            >
                              {getUserInitials(
                                {
                                  first_name: user.first_name,
                                  last_name: user.last_name,
                                  full_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                                  username: user.username,
                                },
                                "U"
                              )}
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{ lineHeight: 1.2 }}
                              >
                                {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.username}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: 13, lineHeight: 1.35 }}
                              >
                                {user.email || "—"}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: 13, mt: 0.35 }}
                              >
                                @{user.username}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Details grid */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 1.5,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                              >
                                Role
                              </Typography>
                              {renderRoleCell(user)}
                            </Box>

                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                              >
                                Territory
                              </Typography>
                              {renderTerritoryCell(user)}
                            </Box>
                          </Box>

                          {/* Status + action */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              pt: 1,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                              >
                                Status
                              </Typography>
                              {renderStatusCell(user)}
                            </Box>

                            <IconButton
                              size="small"
                              sx={{
                                color: "primary.main",
                                bgcolor: "background.paper",
                                boxShadow: 1,
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                              onClick={() => navigate(`/users/${user.id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Card>
                    ))
                  )}
                </Box>
              ) : (
                // -------------------------------------Desktop Table View
                <Table>
                  <TableHead>
                    <TableRow  sx={{
   			 bgcolor: theme.palette.mode === "dark"
      			? "rgba(255,255,255,0.04)"
     			 : "grey.50",
  				}}
				>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        User
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Username
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Role
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Territory
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", textAlign: "right" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          <Typography color="text.secondary" display="block" gutterBottom>
                            {users.length === 0 ? "No users yet." : "No users found."}
                          </Typography>
                          {users.length === 0 && (
                            <Button variant="contained" color="primary" size="small" onClick={() => navigate("/users/new")}>
                              Add your first user
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                      >
                          <TableCell>{renderUserCell(user)}</TableCell>
                          <TableCell>
                            <Typography
  variant="body2"
  sx={{
    color: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(226,232,240,0.95)"
        : "text.primary",
  }}
>
  @{user.username}
</Typography>

                          </TableCell>
                          <TableCell>{renderRoleCell(user)}</TableCell>
                          <TableCell>
                            {renderTerritoryCell(user)}
                          </TableCell>
                          <TableCell>{renderStatusCell(user)}</TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            <IconButton
  size="small"
  sx={{ color: "primary.main" }}
  onClick={(e) => {
    e.stopPropagation();
    navigate(`/users/${user.id}/edit`);
  }}
>
  <EditIcon fontSize="small" />
</IconButton>

                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </Card>

      {/* ---------------------------------------------------------------------------------------------------------------Edit User Dialog */}
      <CrmDialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="First Name"
                value={selectedUser.first_name || ""}
                onChange={(e) => handleEditField("first_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={selectedUser.last_name || ""}
                onChange={(e) => handleEditField("last_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Username"
                value={selectedUser.username || ""}
                onChange={(e) => handleEditField("username", e.target.value)}
                fullWidth
              />
              <TextField
                label="Email"
                value={selectedUser.email || ""}
                onChange={(e) => handleEditField("email", e.target.value)}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={editGroups[0] ?? ""}
                  onChange={(e) => setEditGroups([Number(e.target.value)])}
                  input={<OutlinedInput label="Role" />}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="territory-select-label">Territory</InputLabel>
                <Select
                  labelId="territory-select-label"
                  value={selectedUser.territory ?? ""}
                  onChange={(e) => handleTerritoryChange(parseInt(e.target.value) || null)}
                  input={<OutlinedInput label="Territory" />}
                >
                  <MenuItem value="">
                    <em>No Territory Assigned</em>
                  </MenuItem>
                  {territories.map((territory) => (
                    <MenuItem key={territory.id} value={territory.id}>
                      {territory.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!selectedUser.is_active}
                    onChange={(e) => handleEditField("is_active", e.target.checked)}
                  />
                }
                label="Active"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ width: "100%", px: 2, pb: 2 }}>

  {/* --------------------------------------------------------------------------------------------------------DESKTOP LAYOUT */}
  <Box
    sx={{
      display: { xs: "none", md: "flex" },
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      width: "100%",
      mt: 1,
    }}
  >
    {/* ------------------------------------------------------------DELETE (left) */}
    {can.canDelete && selectedUser && (
      <Button
        variant="contained"
        color="error"
        sx={{ flex: 1 }}
        onClick={() => {
          setUserToDelete(selectedUser);
          setConfirmDelete(true);
        }}
      >
        Delete User
      </Button>
    )}

    {/* ------------------------------------------------------------CANCEL (middle) */}
    <Button
      variant="outlined"
      color="inherit"
      sx={{ flex: 1 }}
      onClick={() => setOpenEdit(false)}
    >
      Cancel
    </Button>

    {/* ----------------------------------------------------------SAVE (right) */}
    <Button
      variant="contained"
      color="primary"
      sx={{ flex: 1 }}
      onClick={handleSaveUser}
    >
      Save Changes
    </Button>
  </Box>

  {/* ----------------------------------------------------------------------------------------------------------MOBILE LAYOUT */}
  <Box
    sx={{
      display: { xs: "flex", md: "none" },
      flexDirection: "column",
      gap: 2,
      p: 2,
      width: "100%",
      alignItems: "stretch",
      mt: 2,
    }}
  >
    <Button
      variant="contained"
      color="primary"
      sx={{ width: "100%" }}
      onClick={handleSaveUser}
    >
      Save Changes
    </Button>

    <Button
      variant="outlined"
      color="inherit"
      sx={{ width: "100%" }}
      onClick={() => setOpenEdit(false)}
    >
      Cancel
    </Button>

    {can.canDelete && selectedUser && (
      <Button
        variant="contained"
        color="error"
        sx={{ width: "100%" }}
        onClick={() => {
          setUserToDelete(selectedUser);
          setConfirmDelete(true);
        }}
      >
        Delete User
      </Button>
    )}
  </Box>

</DialogActions>

      </CrmDialog>

      {/* ----------------------------------------------------------------------------------------------------------Confirm Delete */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>
          Are you sure you want to delete user <b>{userToDelete?.username}</b>?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
</Box>
  );
}
